/**
 * Quiz answer pre-seeding from the URL — used by landing pages that embed
 * the first quiz question directly on the page (Timeleft-style funnels).
 *
 * The landing page encodes the tapped answer as `?pre=questionId:value`
 * (comma-separated for multiple) and deep-links into the quiz. The quiz
 * seeds those answers into its initial state and the stepper skips the
 * already-answered question, so the user continues from question two.
 *
 * Only whitelisted single-select questions/values are accepted — anything
 * else in the param is ignored, so a mangled URL can never inject junk
 * into the profile.
 */

export const PREFILL_PARAM = "pre";

const PRESEEDABLE: Record<string, readonly string[]> = {
  socialBattery: ["always_charged", "energized", "balance", "small_groups"],
  dinnerVibe: ["life_of_table", "chill_engaged", "listener"],
  // Not asked in onboarding (profile-only elsewhere) but a valid profile
  // field the vibe scorer reads — the quiz-first landing's fun opener.
  groupChatRole: ["memes", "hype", "thoughtful", "inclusive"],
};

export function buildPrefillParam(questionId: string, value: string): string {
  return `${PREFILL_PARAM}=${encodeURIComponent(`${questionId}:${value}`)}`;
}

/** Parse + validate `?pre=` from the current URL. Returns {} when absent/invalid. */
export function getPreseededAnswers(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const raw = new URLSearchParams(window.location.search).get(PREFILL_PARAM);
  if (!raw) return {};
  const seeded: Record<string, string> = {};
  for (const pair of raw.split(",")) {
    const [questionId, value] = pair.split(":").map((s) => s?.trim());
    if (!questionId || !value) continue;
    if (PRESEEDABLE[questionId]?.includes(value)) {
      seeded[questionId] = value;
    }
  }
  return seeded;
}
